import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import tinycolor from 'tinycolor2';
import tinygradient from 'tinygradient-es';
import { isBrowser } from '@acorex/core/platform';

function AXAutoUnsubscriber() {
    return function (constructor) {
        const orig = constructor.prototype.ngOnDestroy;
        constructor.prototype.ngOnDestroy = function () {
            for (const prop in this) {
                const property = this[prop];
                if (typeof property.subscribe === 'function') {
                    property.unsubscribe();
                }
            }
            orig.apply();
        };
    };
}
class AXUnsubscriber {
    constructor() {
        this._destroy$ = new Subject();
        this.takeUntilDestroy = (origin) => origin.pipe(takeUntil(this._destroy$));
    }
    unsubscribe() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    ngOnDestroy() {
        this.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXUnsubscriber, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXUnsubscriber }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXUnsubscriber, decorators: [{
            type: Injectable
        }] });

class AXColorUtil {
    static to(color, mode) {
        const _color = tinycolor(color);
        switch (mode) {
            case 'rgba':
                return _color.toRgb();
            case 'hsla':
                return _color.toHsl();
            case 'hsva':
                return _color.toHsv();
            default:
                return _color.toHex();
        }
    }
    static toString(color, mode = null) {
        const _color = tinycolor(color);
        switch (mode) {
            case 'rgba':
                return _color.toRgbString();
            case 'hsla':
                return _color.toHslString();
            case 'hsva':
                return _color.toHsvString();
            case 'hex': {
                const rgba = _color.toRgb();
                return rgba.a != 1 ? _color.toHex8String() : _color.toHexString();
            }
            default: {
                if (typeof color == 'string') {
                    if (color.toLowerCase().startsWith('#'))
                        return this.toString(color, 'hex');
                    else
                        return this.toString(color, 'rgba');
                }
                else {
                    return this.toString(color, 'rgba');
                }
            }
        }
    }
    static isValid(color) {
        const _color = tinycolor(color);
        return _color.isValid();
    }
    static mix(baseColor, hex, percentage) {
        return tinycolor.mix(baseColor, hex, percentage).toString('rgb');
    }
    static multiply(color1, color2) {
        const rgb1 = tinycolor(color1).toRgb();
        const rgb2 = tinycolor(color2).toRgb();
        rgb1.b = Math.floor((rgb1.b * rgb2.b) / 255);
        rgb1.g = Math.floor((rgb1.g * rgb2.g) / 255);
        rgb1.r = Math.floor((rgb1.r * rgb2.r) / 255);
        return tinycolor('rgb ' + rgb1.r + ' ' + rgb1.g + ' ' + rgb1.b).toString('rgb');
    }
    static contrastToWhite(color) {
        return tinycolor.readability('#fff', color);
    }
    static lighten(hex, percentage) {
        return tinycolor(hex).lighten(percentage);
    }
    static darken(hex, percentage) {
        return tinycolor(hex).darken(percentage);
    }
    static brighten(hex, percentage) {
        return tinycolor(hex).brighten(percentage);
    }
    static saturate(hex, percentage) {
        return tinycolor(hex).saturate(percentage);
    }
    static desaturate(hex) {
        return tinycolor(hex).getLuminance();
    }
    static equal(color1, color2) {
        return tinycolor.equals(color1, color2);
    }
    static gradient(values) {
        return tinygradient([...values]);
    }
    static getLuminance(hex) {
        return tinycolor(hex).getLuminance();
    }
    static xyToRgb(vX, vY) {
        vY = vY || 0.00000000001;
        const Y = 1;
        const X = (Y / vY) * vX;
        const Z = (Y / vY) * (1 - vX - vY);
        // Convert to RGB using Wide RGB D65 conversion.
        let rgb = [
            X * 1.656492 - Y * 0.354851 - Z * 0.255038,
            -X * 0.707196 + Y * 1.655397 + Z * 0.036152,
            X * 0.051713 - Y * 0.121364 + Z * 1.01153,
        ];
        // Apply reverse gamma correction.
        rgb = rgb.map((x) => (x <= 0.0031308 ? 12.92 * x : (1.0 + 0.055) * Math.pow(x, 1.0 / 2.4) - 0.055));
        // Bring all negative components to zero.
        rgb = rgb.map((x) => Math.max(0, x));
        // If one component is greater than 1, weight components by that value.
        const max = Math.max(...rgb);
        if (max > 1) {
            rgb = rgb.map((x) => x / max);
        }
        rgb = rgb.map((x) => Math.round(x * 255));
        return 'rgb(' + rgb.join(',') + ')';
    }
}

// @dynamic
class AXDrawingUtil {
    static collision(a, b) {
        const ac = a.getBoundingClientRect();
        const bc = b.getBoundingClientRect();
        if (ac.left < bc.left + bc.width && ac.left + ac.width > bc.left && ac.top < bc.top + bc.height && ac.top + ac.height > bc.top) {
            return true;
        }
        else {
            return false;
        }
    }
    static isInElementBound(pos, element) {
        const elBound = element.getBoundingClientRect();
        return AXDrawingUtil.isInRecPoint(pos, {
            left: elBound.x,
            width: elBound.width,
            top: elBound.y,
            height: elBound.height,
        });
    }
    static isInRecPoint(pos, rec) {
        return pos.x >= rec.left && pos.x <= rec.left + rec.width && pos.y >= rec.top && pos.y <= rec.top + rec.height;
    }
    static convertRemToPixels(rem) {
        return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
}

// @dynamic
class AXHtmlUtil {
    static focusElement(element) {
        const list = ['button', 'input', '[href]', 'select', 'textarea', '[tabindex]'].map((c) => c + ':not([tabindex="-1"])');
        const focusable = element.querySelector(list.join(', '));
        if (focusable) {
            focusable.focus();
            return focusable;
        }
        return null;
    }
    static blurElement(element) {
        const list = ['button', 'input', '[href]', 'select', 'textarea', '[tabindex]'].map((c) => c + ':not([tabindex="-1"])');
        const focusable = element.querySelector(list.join(', '));
        if (focusable) {
            focusable.blur();
            return focusable;
        }
        return null;
    }
    static hasFocus(element) {
        return element.matches(':focus-within') || element.matches(':focus');
    }
    static isRtl(element) {
        if (isBrowser()) {
            const rtl = element.classList.contains('ax-rtl') || window.getComputedStyle(element, null).getPropertyValue('direction') === 'rtl';
            return rtl;
        }
        return false;
    }
}

// @dynamic
class AXStringUtil {
    static getWordBoundsAtPosition(str, position) {
        const isSpace = (c) => /[^a-zA-Z0-9]+/i.test(c);
        let start = position - 1;
        while (start >= 0 && !isSpace(str[start])) {
            start -= 1;
        }
        start = Math.max(0, start + 1);
        const leftSideString = str.slice(start).match(/[a-zA-Z0-9]+/i);
        start += leftSideString.index;
        let end = start + leftSideString[0].length;
        return {
            start,
            end,
        };
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AXAutoUnsubscriber, AXColorUtil, AXDrawingUtil, AXHtmlUtil, AXStringUtil, AXUnsubscriber };
